import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  BadgeCheck,
  CheckCircle2,
  Clock3,
  Eye,
  Loader2,
  RefreshCw,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { adminAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const STATUS_TABS = [
  { value: 'new', label: 'New', status: 'New' },
  { value: 'review', label: 'In Review', status: 'In Review' },
  { value: 'approved', label: 'Approved', status: 'Approved' },
  { value: 'rejected', label: 'Rejected', status: 'Rejected' },
];

const STATUS_STYLES = {
  New: 'bg-blue-100 text-blue-800 border-blue-200',
  'In Review': 'bg-amber-100 text-amber-800 border-amber-200',
  Approved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Rejected: 'bg-rose-100 text-rose-800 border-rose-200',
};

const formatDateTime = (value) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
};

const formatBytes = (value) => {
  if (!Number.isFinite(value) || value <= 0) return '0 KB';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const detailRow = (label, value) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
    <p className="mt-1 break-words text-sm text-slate-800">{value || '-'}</p>
  </div>
);

const MobilityApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('new');
  const [actionLoading, setActionLoading] = useState(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    const hasToken = localStorage.getItem('adminToken');
    if (!isLoggedIn || !hasToken) {
      navigate('/admin');
      return;
    }
    fetchApplications();
  }, [navigate]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminAPI.getMobilityApplications();
      setApplications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching mobility applications:', err);
      if (err.message === 'Authentication required') {
        localStorage.removeItem('adminLoggedIn');
        localStorage.removeItem('adminUsername');
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        setError('Failed to load mobility applications. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const applicationId = searchParams.get('id');
    if (applicationId && applications.length > 0 && !viewDialog) {
      const application = applications.find((item) => item.id === applicationId);
      if (application) {
        setSelectedApplication(application);
        setViewDialog(true);
        setSearchParams({}, { replace: true });
      }
    }
  }, [applications, searchParams, viewDialog, setSearchParams]);

  const groupedApplications = useMemo(() => {
    return STATUS_TABS.reduce((acc, tab) => {
      acc[tab.value] = applications.filter((application) => (application.status || 'New') === tab.status);
      return acc;
    }, {});
  }, [applications]);

  const handleView = async (application) => {
    try {
      setActionLoading(application.id);
      const detailed = await adminAPI.getMobilityApplication(application.id);
      setSelectedApplication(detailed || application);
      setViewDialog(true);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load application details',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const updateStatus = async (applicationId, status) => {
    setActionLoading(applicationId);
    try {
      await adminAPI.updateMobilityApplicationStatus(applicationId, status);
      toast({
        title: 'Success',
        description: `Application marked as ${status.toLowerCase()}`,
      });
      await fetchApplications();
      if (selectedApplication?.id === applicationId) {
        setSelectedApplication((current) => current ? { ...current, status } : current);
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to update application status',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const deleteApplication = async (applicationId) => {
    if (!window.confirm('Delete this mobility application? This cannot be undone.')) return;

    setActionLoading(applicationId);
    try {
      await adminAPI.deleteMobilityApplication(applicationId);
      toast({
        title: 'Success',
        description: 'Application deleted successfully',
      });
      await fetchApplications();
      if (selectedApplication?.id === applicationId) {
        setViewDialog(false);
        setSelectedApplication(null);
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to delete application',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const renderApplicationCard = (application) => {
    const status = application.status || 'New';

    return (
      <Card key={application.id} className="border-slate-200 transition-shadow hover:shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-lg text-slate-900">{application.fullName}</CardTitle>
              <CardDescription className="text-sm">{application.emailId}</CardDescription>
            </div>
            <Badge className={STATUS_STYLES[status] || STATUS_STYLES.New}>{status}</Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          <div className="grid gap-2 text-sm text-slate-600">
            <p>
              <span className="font-medium text-slate-900">Institution:</span> {application.institutionName}
            </p>
            <p>
              <span className="font-medium text-slate-900">Country:</span> {application.country}
            </p>
            <p>
              <span className="font-medium text-slate-900">Visit:</span> {application.visitFrom} to {application.visitTo}
            </p>
            <p>
              <span className="font-medium text-slate-900">Submitted:</span> {formatDateTime(application.createdAt)}
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            <p className="font-medium text-slate-900">Documents</p>
            <p className="mt-1">
              {[
                application.lastSemMarksheet?.name,
                application.passportCopy?.name,
                application.passportPhoto?.name,
              ].filter(Boolean).length}
              /3 uploaded as metadata
            </p>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button size="sm" variant="outline" className="flex-1" onClick={() => handleView(application)}>
              <Eye className="mr-1 h-4 w-4" />
              View
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={() => updateStatus(application.id, 'In Review')}
              disabled={actionLoading === application.id}
            >
              {actionLoading === application.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock3 className="mr-1 h-4 w-4" />}
              Review
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              onClick={() => updateStatus(application.id, 'Approved')}
              disabled={actionLoading === application.id}
            >
              <BadgeCheck className="mr-1 h-4 w-4" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="flex-1"
              onClick={() => updateStatus(application.id, 'Rejected')}
              disabled={actionLoading === application.id}
            >
              <XCircle className="mr-1 h-4 w-4" />
              Reject
            </Button>
          </div>

          <div className="pt-2 border-t border-slate-100">
            <Button
              size="sm"
              variant="ghost"
              className="w-full text-rose-700 hover:bg-rose-50 hover:text-rose-800"
              onClick={() => deleteApplication(application.id)}
              disabled={actionLoading === application.id}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderDetailSection = (title, description, children) => (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg text-slate-900">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-medicaps-blue" />
          <p className="text-slate-600">Loading mobility applications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <span className="text-2xl text-red-600">!</span>
          </div>
          <h3 className="mb-2 text-xl font-semibold text-slate-900">Failed to Load Applications</h3>
          <p className="mb-6 text-slate-600">{error}</p>
          <Button onClick={fetchApplications} className="bg-medicaps-blue hover:bg-medicaps-blue/90">
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const application = selectedApplication;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/admin/dashboard')} variant="outline" className="border-slate-300">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Mobility Applications</h1>
                <p className="text-slate-600">Review student mobility submissions inside the website</p>
              </div>
            </div>
            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
              {applications.length} Total Applications
            </Badge>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {STATUS_TABS.map((tab) => (
            <Card key={tab.value} className="border-slate-200">
              <CardContent className="py-4 text-center">
                <div className="text-2xl font-bold text-medicaps-blue">{groupedApplications[tab.value]?.length || 0}</div>
                <p className="text-sm text-slate-600">{tab.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            {STATUS_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label} ({groupedApplications[tab.value]?.length || 0})
              </TabsTrigger>
            ))}
          </TabsList>

          {STATUS_TABS.map((tab) => (
            <TabsContent key={tab.value} value={tab.value}>
              {groupedApplications[tab.value]?.length > 0 ? (
                <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
                  {groupedApplications[tab.value].map(renderApplicationCard)}
                </div>
              ) : (
                <Card className="border-dashed border-slate-300 bg-white">
                  <CardContent className="py-12 text-center text-slate-500">
                    No {tab.label.toLowerCase()} applications yet.
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3">
              <span>Mobility Application Details</span>
              {application ? (
                <Badge className={STATUS_STYLES[application.status || 'New'] || STATUS_STYLES.New}>
                  {application.status || 'New'}
                </Badge>
              ) : null}
            </DialogTitle>
            <DialogDescription>
              Full review view with the current frontend-only document metadata and submission details.
            </DialogDescription>
          </DialogHeader>

          {application ? (
            <div className="space-y-6">
              {renderDetailSection(
                'Submission Overview',
                'Core identifiers and submission timestamps.',
                <div className="grid gap-4 md:grid-cols-2">
                  {detailRow('Full Name', application.fullName)}
                  {detailRow('Email', application.emailId)}
                  {detailRow('Contact Number', application.contactNumber)}
                  {detailRow('Status', application.status)}
                  {detailRow('Submitted', formatDateTime(application.createdAt))}
                  {detailRow('Updated', formatDateTime(application.updatedAt))}
                </div>
              )}

              {renderDetailSection(
                'Personal Details',
                'Applicant identity and contact information.',
                <div className="grid gap-4 md:grid-cols-2">
                  {detailRow('Gender', application.gender)}
                  {detailRow('Date of Birth', application.dateOfBirth)}
                  {detailRow('Nationality', application.nationality)}
                  {detailRow('Country', application.country)}
                  {detailRow('City', application.city)}
                  {detailRow('State / Province', application.stateProvince)}
                  {detailRow('PIN / ZIP Code', application.pinCode)}
                  <div className="md:col-span-2">{detailRow('Postal Address', application.postalAddress)}</div>
                </div>
              )}

              {renderDetailSection(
                'Academic Details',
                'Current institution and research information.',
                <div className="grid gap-4 md:grid-cols-2">
                  {detailRow('Roll Number', application.rollNumber)}
                  {detailRow('Enrolment Date', application.enrolmentDate)}
                  {detailRow('Current Semester', application.currentSemester)}
                  {detailRow('Institution Name', application.institutionName)}
                  <div className="md:col-span-2">{detailRow('Institution Address', application.institutionAddress)}</div>
                  <div className="md:col-span-2">{detailRow('Area of Research / Study', application.areaOfResearch)}</div>
                </div>
              )}

              {renderDetailSection(
                'Passport Information',
                'Travel document details used for mobility processing.',
                <div className="grid gap-4 md:grid-cols-2">
                  {detailRow('Passport Number', application.passportNumber)}
                  {detailRow('Passport Country', application.passportCountry)}
                  {detailRow('Issue Date', application.passportIssueDate)}
                  {detailRow('Expiry Date', application.passportExpiryDate)}
                  {detailRow('Place of Issue', application.passportPlaceOfIssue)}
                </div>
              )}

              {renderDetailSection(
                'Mobility / Visit Details',
                'Planned visit window and funding source.',
                <div className="grid gap-4 md:grid-cols-2">
                  {detailRow('Duration', application.duration)}
                  {detailRow('Visit From', application.visitFrom)}
                  {detailRow('Visit To', application.visitTo)}
                  {detailRow('Funding Source', application.fundingSource)}
                </div>
              )}

              {renderDetailSection(
                'Documents',
                'Documents are uploaded to Cloudinary and stored as secure URLs in the backend.',
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Last Semester Marksheet</p>
                    <p className="mt-1 break-words text-sm text-slate-800">
                      {application.lastSemMarksheet?.name
                        ? `${application.lastSemMarksheet.name} (${formatBytes(application.lastSemMarksheet.size)})`
                        : 'Not uploaded'}
                    </p>
                    {application.lastSemMarksheet?.url ? (
                      <a
                        href={application.lastSemMarksheet.url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-block text-xs font-medium text-medicaps-blue hover:underline"
                      >
                        Open file
                      </a>
                    ) : null}
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Passport Copy</p>
                    <p className="mt-1 break-words text-sm text-slate-800">
                      {application.passportCopy?.name
                        ? `${application.passportCopy.name} (${formatBytes(application.passportCopy.size)})`
                        : 'Not uploaded'}
                    </p>
                    {application.passportCopy?.url ? (
                      <a
                        href={application.passportCopy.url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-block text-xs font-medium text-medicaps-blue hover:underline"
                      >
                        Open file
                      </a>
                    ) : null}
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Passport Photo</p>
                    <p className="mt-1 break-words text-sm text-slate-800">
                      {application.passportPhoto?.name
                        ? `${application.passportPhoto.name} (${formatBytes(application.passportPhoto.size)})`
                        : 'Not uploaded'}
                    </p>
                    {application.passportPhoto?.url ? (
                      <a
                        href={application.passportPhoto.url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-block text-xs font-medium text-medicaps-blue hover:underline"
                      >
                        Open file
                      </a>
                    ) : null}
                  </div>
                  {detailRow('Declaration', application.agreeToDeclaration ? 'Accepted' : 'Not accepted')}
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                <Button onClick={() => updateStatus(application.id, 'In Review')} variant="outline">
                  <Clock3 className="mr-2 h-4 w-4" />
                  Mark In Review
                </Button>
                <Button onClick={() => updateStatus(application.id, 'Approved')} className="bg-emerald-600 hover:bg-emerald-700">
                  <BadgeCheck className="mr-2 h-4 w-4" />
                  Approve
                </Button>
                <Button onClick={() => updateStatus(application.id, 'Rejected')} variant="destructive">
                  <XCircle className="mr-2 h-4 w-4" />
                  Reject
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobilityApplications;
